/* ==========================================================================
   MSP Supply — Pumps, Filters & Heaters interactive sizing center
   Vanilla JS, scoped under window.MSPPFH. No external libraries.
   All formulas are commented at their point of use. Every calculated
   number is guarded against NaN/Infinity/negative inputs before display.
   ========================================================================== */
(function () {
  "use strict";

  /* ------------------------------------------------------------------ *
   * Shared state — one source of truth so Section 1 (runtime), Section 2
   * (pool/pump/filter sizing) and Section 3 (heater) all stay in sync
   * WHEN they happen to be on the same page together. Each is also its
   * own independent Shopify section (sections/pfh-*.liquid) that can be
   * added anywhere on its own — see sizingSectionPresent() below for how
   * the heater section falls back to its own manual inputs when Section 2
   * isn't present.
   * ------------------------------------------------------------------ */
  var state = {
    // Pool volume / shape (Section 2 is the source; Section 1 + 3 reuse it)
    shape: "rectangle", // rectangle | round | oval
    dims: {
      rectangle: { length: 32, width: 16, shallow: 3, deep: 8 },
      round: { diameter: 24, shallow: 3, deep: 8 },
      oval: { longD: 32, shortD: 18, shallow: 3, deep: 8 }
    },
    manualGallons: null, // set when the user types directly into a gallons field
    gallonsSource: "calculated", // "calculated" | "manual"

    // Turnover goal, shared between Section 1 and Section 2
    turnoverHours: 8,

    // Section 1 — pump speed
    speedPercent: 60,

    // Section 2 — system resistance + plumbing
    tdhLevel: "standard", // simple | standard | complex | advanced
    tdhAdvancedFt: 55,
    pipeSize: "2", // "1.5" | "2" | "advanced"
    pipeAdvancedGpm: 73,

    // Section 2 — active equipment tab
    equipTab: "pump", // pump | filter | heater

    // Section 3 — heater
    currentTemp: 76,
    targetTemp: 84,
    airTemp: 82,
    heaterType: "gas", // gas | heatpump

    // Section 3 — standalone fallback pool size, used ONLY when the
    // Equipment Sizing section isn't present on the page (see
    // sizingSectionPresent() below). Defaults match Section 2's own
    // default rectangle example (32x16, avg depth 5.5) so the two
    // sections feel consistent even when used apart.
    heaterManualArea: 512,
    heaterManualGallons: 21120
  };

  var MAX_RPM = 3450; // typical variable-speed pool pump max RPM (documented estimate)
  var GAS_EFFICIENCY = 0.82; // typical delivered-to-water efficiency for standard gas/propane heaters
  var HEATER_RANGE_BAND = 0.18; // +/- band applied to the point estimate for a realistic range display
  var HEATER_DESIGN_RISE_F = 20; // standard design temperature rise (deg F) used ONLY to estimate a
    // representative heater capacity for the heat-up-TIME math below. A real heater's physical output
    // doesn't change just because the user types a different target temp, so computing "capacity" from
    // the SAME rise being solved for would make hours completely independent of the entered
    // temperatures (it cancels out algebraically: hours = (gal*8.33*rise)/(area*rise*12*eff), and rise
    // divides out). Using a fixed reference rise here keeps heat-up time properly proportional to how
    // far the user is actually asking to heat the pool.

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */
  function clamp(n, min, max) {
    if (isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
  }
  function num(v, fallback) {
    var n = parseFloat(v);
    return isFinite(n) && n >= 0 ? n : fallback;
  }
  function fmt(n, decimals) {
    if (!isFinite(n)) return "—";
    return n.toLocaleString("en-US", {
      minimumFractionDigits: decimals || 0,
      maximumFractionDigits: decimals || 0
    });
  }
  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function $all(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  // Section 2 (Equipment Sizing) is the source of truth for pool shape/
  // volume when it's on the page. Section 3 (Heater) checks this to decide
  // whether to reuse Section 2's live numbers or fall back to its own
  // manual "surface area + gallons" fields, since the two are now
  // independent, separately-reusable sections rather than always-together
  // pieces of one big tool.
  function sizingSectionPresent() {
    return !!document.getElementById("pfh-sizing");
  }

  /* ------------------------------------------------------------------ *
   * Pool volume + surface area
   * Formulas as specified:
   *   Rectangle gallons = L x W x avgDepth x 7.5
   *   Round     gallons = D x D x avgDepth x 5.9
   *   Oval      gallons = longD x shortD x avgDepth x 6.7
   *   avgDepth = (shallow + deep) / 2
   * ------------------------------------------------------------------ */
  function avgDepth(d) {
    return (num(d.shallow, 0) + num(d.deep, 0)) / 2;
  }
  function calcGallons() {
    var d = state.dims[state.shape];
    var depth = avgDepth(d);
    var gallons = 0;
    if (state.shape === "rectangle") {
      gallons = num(d.length, 0) * num(d.width, 0) * depth * 7.5;
    } else if (state.shape === "round") {
      gallons = num(d.diameter, 0) * num(d.diameter, 0) * depth * 5.9;
    } else if (state.shape === "oval") {
      gallons = num(d.longD, 0) * num(d.shortD, 0) * depth * 6.7;
    }
    return Math.max(0, Math.round(gallons));
  }
  function calcSurfaceArea() {
    var d = state.dims[state.shape];
    if (state.shape === "rectangle") {
      return Math.max(0, num(d.length, 0) * num(d.width, 0));
    }
    if (state.shape === "round") {
      var r = num(d.diameter, 0) / 2;
      return Math.max(0, Math.PI * r * r);
    }
    // oval: pi x (longD/2) x (shortD/2)
    return Math.max(0, Math.PI * (num(d.longD, 0) / 2) * (num(d.shortD, 0) / 2));
  }
  function activeGallons() {
    if (state.gallonsSource === "manual" && state.manualGallons != null) {
      return Math.max(0, state.manualGallons);
    }
    return calcGallons();
  }

  /* ------------------------------------------------------------------ *
   * Section 1 — Pump runtime
   * Required flow to hit the chosen turnover goal at "full" pump output:
   *   refFlowGPM = gallons / turnoverHours / 60
   * IMPORTANT: flow does not scale 1:1 with RPM in reality (system-curve
   * interaction). Absent a manufacturer performance curve we use a
   * linear approximation (flow% ~= speed%) and label every dependent
   * number as an estimate.
   * ------------------------------------------------------------------ */
  function calcRuntime() {
    var gallons = activeGallons();
    var refFlowGPM = state.turnoverHours > 0 ? gallons / state.turnoverHours / 60 : 0;
    var speedFraction = clamp(state.speedPercent, 20, 100) / 100;
    var estFlowGPM = refFlowGPM * speedFraction; // linear approximation — clearly labeled as an estimate in the UI
    var turnoverHoursAtSpeed = estFlowGPM > 0 ? gallons / (estFlowGPM * 60) : 0;
    var rpm = Math.round(MAX_RPM * speedFraction);
    return {
      gallons: gallons,
      refFlowGPM: refFlowGPM,
      estFlowGPM: estFlowGPM,
      recommendedRunHours: turnoverHoursAtSpeed,
      turnoverHoursAtSpeed: turnoverHoursAtSpeed,
      rpm: rpm,
      speedFraction: speedFraction
    };
  }

  /* ------------------------------------------------------------------ *
   * Section 2 — Required flow / TDH / pump+filter recommendation
   * ------------------------------------------------------------------ */
  function calcRequiredFlow() {
    var gallons = activeGallons();
    var gph = state.turnoverHours > 0 ? gallons / state.turnoverHours : 0;
    var gpm = gph / 60;
    return { gallons: gallons, gph: gph, gpm: gpm };
  }
  var TDH_RANGES = {
    simple: { label: "Simple system", ft: "40–50 ft", desc: "Pump near pool, minimal equipment and relatively short plumbing." },
    standard: { label: "Standard system", ft: "50–60 ft", desc: "Typical residential pool with filter, sanitizer and normal plumbing." },
    complex: { label: "Complex system", ft: "60–70+ ft", desc: "Long plumbing runs, heater, salt cell, spa, water features, solar, elevated equipment, etc." }
  };
  function tdhFeetEstimate() {
    if (state.tdhLevel === "advanced") return num(state.tdhAdvancedFt, 55);
    if (state.tdhLevel === "simple") return 45;
    if (state.tdhLevel === "complex") return 65;
    return 55; // standard
  }
  function poolSizeTier(gallons) {
    if (gallons <= 10000) return "small";
    if (gallons <= 20000) return "medium";
    return "large";
  }
  var PUMP_GUIDE = {
    small: { range: "5,000–10,000 gallons", pump: "0.5–0.75 HP, or a small variable-speed pump" },
    medium: { range: "10,000–20,000 gallons", pump: "0.75–1.5 HP" },
    large: { range: "20,000–30,000+ gallons", pump: "1.5–3.0 HP" }
  };
  var FILTER_GUIDE = {
    small: { range: "5,000–10,000 gallons", sand: "16″–18″", cartridge: "50–100 sq ft" },
    medium: { range: "10,000–20,000 gallons", sand: "21″–24″", cartridge: "100–150 sq ft" },
    large: { range: "20,000–30,000+ gallons", sand: "24″–30″", cartridge: "150–200+ sq ft" }
  };
  var PLUMBING_GUIDE = {
    "1.5": { label: "1.5″ PVC", gpm: 43, note: "Approximately 42–44 GPM (general guideline)" },
    "2": { label: "2″ PVC", gpm: 73, note: "Approximately 73 GPM (general guideline)" }
  };
  function plumbingGuidelineGpm() {
    if (state.pipeSize === "advanced") return num(state.pipeAdvancedGpm, 73);
    return PLUMBING_GUIDE[state.pipeSize].gpm;
  }

  /* ------------------------------------------------------------------ *
   * Section 3 — Heater sizing
   * Estimated heater size (simplified surface-area method):
   *   BTU/hr = surfaceArea x tempRise x 12
   * Heat-up energy needed (physics: 1 BTU raises 1 lb water 1F, water
   * weighs ~8.33 lb/gal):
   *   totalBTU = gallons x 8.33 x tempRise
   *   hours = totalBTU / deliveredBTUperHour
   * Gas/propane: apply a typical delivered-efficiency factor to the
   * *rated input* BTU because nameplate BTU is an input rating, not
   * what reaches the water. Heat pump: derate by air temperature since
   * heat-pump output drops in cooler air — shown as a range either way.
   * ------------------------------------------------------------------ */
  var HEATER_SIZE_GUIDE = {
    small: { range: "Under 10,000 gallons", gas: "100,000–150,000 BTU", heatpump: "50,000–75,000 BTU" },
    medium: { range: "10,000–20,000 gallons", gas: "200,000–250,000 BTU", heatpump: "90,000–110,000 BTU" },
    large: { range: "20,000–30,000 gallons", gas: "300,000–400,000 BTU", heatpump: "120,000–140,000 BTU" }
  };
  function heaterSizeTier(gallons) {
    if (gallons < 10000) return "small";
    if (gallons <= 20000) return "medium";
    return "large";
  }
  function calcHeater() {
    // Reuse Section 2's live pool numbers when it's on the page; otherwise
    // fall back to this section's own manual area/gallons fields so the
    // heater calculator works correctly as a fully standalone section too.
    var sizingPresent = sizingSectionPresent();
    var gallons = sizingPresent ? activeGallons() : Math.max(0, num(state.heaterManualGallons, 0));
    var area = sizingPresent ? calcSurfaceArea() : Math.max(0, num(state.heaterManualArea, 0));
    var rise = Math.max(0, num(state.targetTemp, 0) - num(state.currentTemp, 0));
    var estimatedBtuPerHr = area * rise * 12; // simplified surface-area sizing estimate

    var totalBtuNeeded = gallons * 8.33 * rise; // energy to raise the whole body of water

    // Reference heater capacity for the heat-up-time math — sized using the fixed design rise
    // above, NOT the live "rise" var, so that hours actually responds to the user's entered
    // temperatures instead of canceling out. (See HEATER_DESIGN_RISE_F comment for why.)
    var referenceBtuPerHr = area * HEATER_DESIGN_RISE_F * 12;

    var deliveredBtuPerHr;
    if (state.heaterType === "gas") {
      deliveredBtuPerHr = referenceBtuPerHr * GAS_EFFICIENCY;
    } else {
      // Heat pump output derates in cooler air. Simple, documented model:
      // 50F air -> ~55% of rated output, 90F air -> 100%. Clamped 0.5-1.0.
      var airTemp = num(state.airTemp, 80);
      var hpFactor = clamp((airTemp - 50) / 40 * 0.45 + 0.55, 0.5, 1.0);
      deliveredBtuPerHr = referenceBtuPerHr * hpFactor;
    }

    var pointHours = deliveredBtuPerHr > 0 ? totalBtuNeeded / deliveredBtuPerHr : 0;
    var lowHours = pointHours * (1 - HEATER_RANGE_BAND);
    var highHours = pointHours * (1 + HEATER_RANGE_BAND);

    return {
      gallons: gallons,
      area: area,
      rise: rise,
      estimatedBtuPerHr: estimatedBtuPerHr,
      totalBtuNeeded: totalBtuNeeded,
      deliveredBtuPerHr: deliveredBtuPerHr,
      lowHours: lowHours,
      highHours: highHours,
      tier: heaterSizeTier(gallons)
    };
  }

  /* ------------------------------------------------------------------ *
   * Expose for the render module (loaded right after this file)
   * ------------------------------------------------------------------ */
  window.MSPPFH = {
    state: state,
    clamp: clamp,
    num: num,
    fmt: fmt,
    $: $,
    $all: $all,
    sizingSectionPresent: sizingSectionPresent,
    calcGallons: calcGallons,
    calcSurfaceArea: calcSurfaceArea,
    activeGallons: activeGallons,
    calcRuntime: calcRuntime,
    calcRequiredFlow: calcRequiredFlow,
    tdhFeetEstimate: tdhFeetEstimate,
    poolSizeTier: poolSizeTier,
    plumbingGuidelineGpm: plumbingGuidelineGpm,
    calcHeater: calcHeater,
    TDH_RANGES: TDH_RANGES,
    PUMP_GUIDE: PUMP_GUIDE,
    FILTER_GUIDE: FILTER_GUIDE,
    PLUMBING_GUIDE: PLUMBING_GUIDE,
    HEATER_SIZE_GUIDE: HEATER_SIZE_GUIDE,
    MAX_RPM: MAX_RPM
  };
})();
